import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { PasswordStrengthIndicator } from "../signup/PasswordStrengthIndicator";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { authRedirectGate } from "../../utils/authRedirectGate";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmPasswordReset, isLoading, user, logout } = useAuth();
  const didClearRef = useRef(false);

  const [uid, setUid] = useState("");
  const [token, setToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    retype_password: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uidParam = params.get("uid");
    const tokenParam = params.get("token");

    if (uidParam && tokenParam) {
      setUid(uidParam);
      setToken(tokenParam);

      const cleanUrl = location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    const hasMinLength = formData.password.length >= 8;
    const hasUppercase = /[A-Z]/.test(formData.password);
    const hasLowercase = /[a-z]/.test(formData.password);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);

    if (!(hasMinLength && hasUppercase && hasLowercase && hasSymbol)) {
      setError("Password does not meet all strength requirements.");
      setIsSubmitting(false);
      return;
    }

    if (formData.password !== formData.retype_password) {
      setError("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    try {
      await confirmPasswordReset({
        uid,
        token,
        newPassword: formData.password,
      });

      setSuccessMessage("Your password has been successfully reset!");
      setFormData({ password: "", retype_password: "" });

      // Redirect to login after short delay
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    authRedirectGate.suppress401Redirect = true;
    return () => {
      authRedirectGate.suppress401Redirect = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoading && user && !didClearRef.current) {
      didClearRef.current = true;

      (async () => {
        try {
          // clears HttpOnly cookies on server
          await logout?.();
        } catch (e) {
        } finally {
          // clear client-side leftovers
          localStorage.clear();
          sessionStorage.clear();
        }
      })();
    }
  }, [isLoading, user, logout]);
  // if (!isLoading && user) {
  //   return <Navigate to="/dashboard" replace />;
  // }

  return (
    <div
      className="h-screen w-full flex items-center justify-center"
      style={{
        background: "linear-gradient(to bottom left, #413072, #08080B)",
      }}
    >
      <div className="bg-white max-w-[600px] w-full space-y-6 p-6 md:p-8 shadow-[0px_0px_20px_#000a] rounded-2xl max-md:mx-5">
        <div className="!mt-0">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Reset Password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Create a new password for your Xenorize account.
          </p>
        </div>

        {!successMessage ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="mb-2">
              <label htmlFor="password" className="block text-sm mb-1">
                New Password
              </label>
              <div className="relative -mt-2">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="New password"
                  onChange={handleChange}
                  required
                  className="w-full border rounded-md px-3 py-2 !pr-[2.5rem]"
                />
                {showPassword ? (
                  <EyeSlashIcon
                    className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                ) : (
                  <EyeIcon
                    className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                )}
              </div>
            </div>
            {!!formData.password.length && (
              <div className="!mt-[-.75rem] !mb-[1rem]">
                <PasswordStrengthIndicator password={formData.password} />
              </div>
            )}

            <div className="mb-4 !mt-0">
              <label htmlFor="retype_password" className="block text-sm mb-1">
                Confirm New Password
              </label>
              <div className="relative -mt-2">
                <input
                  id="retype_password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  onChange={handleChange}
                  required
                  className="w-full border rounded-md px-3 py-2 !pr-[2.5rem]"
                />
                {showConfirmPassword ? (
                  <EyeSlashIcon
                    className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                ) : (
                  <EyeIcon
                    className="w-[16px] h-[16px] absolute top-[50%] translate-y-[-50%] right-4 cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                )}
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full relative overflow-hidden py-3 font-bold text-base bg-[#007A78] rounded-lg text-white"
              >
                {isSubmitting ? "Changing..." : "Change Password"}
              </button>
            </div>
          </form>
        ) : (
          <div className="text-center space-y-4">
            <h3 className="text-green-600 font-bold !mt-[-.5rem]">
              {successMessage}
            </h3>
            <p className="text-sm text-gray-600">
              Redirecting you to login page...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
